import React from 'react';
import isNil from 'lodash/isNil';
import { PURPLE } from '@atlassian/jira-issue-epic-color-types/src/types.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { isEpicHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { Card } from '../../../../model/types.tsx';
import { useBacklogSelector } from '../../../../state/index.tsx';
import { makeGetCardHierarchyLevel } from '../../../../state/selectors/cards/index.tsx';
import { issueParentsSelector } from '../../../../state/selectors/issue-parent/index.tsx';
import EpicLozengeField from '../../card/card-contents/epic-lozenge-field/index.tsx';
import ParentFieldStatic from '../../card/card-contents/parent-field/parent-field-static/index.tsx';
import {
	getLabelWidthMultiplier,
	LabelType,
} from '../../card/card-contents/utils/responsive-labels/index.tsx';
import { EpicWrapper } from '../../card/styled.tsx';
import type { ScreenSize } from '../../hooks/common/constants.tsx';

const EpicLozengeForAllNonEpicsLite = ({
	card,
	isCompactView,
	screenSize,
}: {
	card: Card;
	isCompactView: boolean;
	screenSize: ScreenSize | undefined;
}) => {
	const parents = useBacklogSelector(issueParentsSelector);
	const [parentValue] = useFieldValue({
		issueKey: card.key,
		fieldKey: PARENT_TYPE,
	});

	const mappedParent = card.parentId
		? parents.find(({ id }) => Number(id) === Number(card.parentId))
		: null;

	// Check if the card and parent are different as `useFieldValue` can fail and fallback to
	// the current card's summary which is VERY wrong. We need to check this and not show
	// a lozenge if that happens.
	const isParentValueSameAsCard = card.id === parentValue?.id || card.key === parentValue?.key;

	// Use mapped parent first, fallback to parentValue
	const color = mappedParent?.color || parentValue?.color || PURPLE;
	let summary: string | undefined = mappedParent?.summary;

	// If we couldn't get summary from mapped parent, try to add it from parentValue if
	// we got a valid value from useFieldValue.
	if (!summary && !isParentValueSameAsCard) {
		summary = parentValue?.fields?.summary;
	}

	// There are multiple ways that the backlog will fetch the epic. It will either exist in redux
	// OR we will need to retrieve it from the issue fields sweet state.
	const hasValidEpic = !isNil(summary) && !isNil(color);

	return (
		// Using placeholderWidthMultiplier so that with change in container width the width of placeholder changes
		<EpicWrapper widthMultiplier={getLabelWidthMultiplier(LabelType.EPIC, screenSize)}>
			{hasValidEpic && (
				<ParentFieldStatic
					color={color}
					summary={summary ?? ''} // For TypeScript..
					isCompactView={isCompactView}
				/>
			)}
		</EpicWrapper>
	);
};

type EpicLiteProps = {
	card: Card;
	isCompactView: boolean;
	screenSize: ScreenSize | undefined;
};

export const EpicLite = (props: EpicLiteProps) => {
	const { card, screenSize } = props;
	const cardHierarchyLevel = useBacklogSelector(makeGetCardHierarchyLevel);
	const isEpic = isEpicHierarchyLevel(cardHierarchyLevel(card.id));

	if (isEpic) {
		return (
			<EpicWrapper widthMultiplier={getLabelWidthMultiplier(LabelType.EPIC, screenSize)}>
				<EpicLozengeField issueId={card.id} issueKey={card.key} {...props} />
			</EpicWrapper>
		);
	}

	return <EpicLozengeForAllNonEpicsLite {...props} />;
};
